import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import { Button, Icon, Label, Loader, Popup } from "semantic-ui-react";
import moment from "moment";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTire } from "@fortawesome/pro-light-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

import AppService from "./../../modules/Appointments/service";
import { SEASONS } from "./../../modules/Appointments/common";
import { Can } from "../../modules";
import DBBAppointmentDateModal from "./components/DBBAppointmentDateModal";
import "./DBBButton.css";

const seasonToDBBTyreType = season => {
  if (season === SEASONS.SUMMER) return "S";
  if (season === SEASONS.WINTER) return "W";
  return "A";
};

const shouldFetchTyreLimits = car => Boolean(car?.has_dbb && !car.dbb_appointment_date && !car.dbb_tyre_pickup_requested_at);

class DBBButton extends Component {
  state = {
    tyreLimits: [],
    isFetchingTyreLimits: false,
    isDBBAppointmentDateModalOpen: false,
  };

  componentDidMount() {
    if (shouldFetchTyreLimits(this.props.appointment.car)) this.getDBBTyreLimits();
  }

  componentDidUpdate(prevProps) {
    if (shouldFetchTyreLimits(this.props.appointment.car) && !shouldFetchTyreLimits(prevProps.appointment.car)) this.getDBBTyreLimits();
  }

  getDBBTyreLimits = () => {
    this.setState({ isFetchingTyreLimits: true });

    AppService.getDBBTyreLimits({ appointment_id: this.props.appointment.id })
      .then(result => this.setState({ tyreLimits: result?.data?.data?.tyre_limits || [], isFetchingTyreLimits: false }))
      .catch(error => {
        this.setState({ isFetchingTyreLimits: false });
        console.warn("Error getting DBB tyre limits", error);
      });
  };

  getStorageTyres = () => {
    const inStorageTyres = this.props.appointment.car?.in_storage_tyres;

    if (!inStorageTyres) return [];

    return [
      { tyre: inStorageTyres.front_left_tyre, profile: inStorageTyres.fl_profile },
      { tyre: inStorageTyres.front_right_tyre, profile: inStorageTyres.fr_profile },
      { tyre: inStorageTyres.rear_left_tyre, profile: inStorageTyres.rl_profile },
      { tyre: inStorageTyres.rear_right_tyre, profile: inStorageTyres.rr_profile },
    ];
  };

  hasStorageTyreBelowNecessaryTreadDepth = () => {
    const { tyreLimits } = this.state;

    return this.getStorageTyres().some(({ tyre, profile }) => {
      if (!tyre) return false;

      const tyreType = seasonToDBBTyreType(tyre.season);

      return tyreLimits.some(tyreLimit => tyreLimit.TyreType === tyreType && profile <= tyreLimit.NecessaryTreadDepth);
    });
  };

  canCreateLocally = () => {
    const { car } = this.props.appointment;

    return Boolean(
      car?.has_dbb && !car.dbb_appointment_date && !car.dbb_tyre_pickup_requested_at && this.state.tyreLimits.length && !this.hasStorageTyreBelowNecessaryTreadDepth()
    );
  };

  getDBBLink = () => {
    if (!this.props.onError) return;

    if (!this.props.appointment.id) return this.props.onError("No appointment ID provided");

    AppService.getDBBLink({ id: this.props.appointment.id })
      .then(result => {
        if (!result?.data?.data?.url) {
          this.props.onError("An unknown error occured, please try again");
        }

        let t = window.open(result.data.data.url, "_blank");
        t.focus();
      })
      .catch(error => {
        let errorMsg = error.response && error.response.data ? error.response.data.errors[0] : "Error getting DBB link";
        this.props.onError(errorMsg);
        console.warn("Error getting DBB link for car.", error);
      });
  };

  refreshDBB = () => {
    if (!this.props.onError) return;

    if (!this.props.appointment.id) return this.props.onError("No appointment ID provided");

    AppService.refreshDBB({ id: this.props.appointment.id })
      .then(result => {
        if (!result || !result.data || !result.data.data) {
          this.props.onError("An unknown error occured, please try again");
        }

        this.props.onCarUpdate(result.data.data);
      })
      .catch(error => {
        let errorMsg = error.response && error.response.data ? error.response.data.errors[0] : "Error refreshing DBB";
        this.props.onError(errorMsg);
        console.warn("Error refreshing DBB", error);
      });
  };

  handlePortalClick = () => {
    if (this.canCreateLocally()) return this.setState({ isDBBAppointmentDateModalOpen: true });

    this.getDBBLink();
  };

  handleRefreshClick = () => {
    this.refreshDBB();
  };

  render() {
    let { appointment, location, onCarUpdate, onError, t } = this.props;
    let { isFetchingTyreLimits, isDBBAppointmentDateModalOpen } = this.state;
    let status = "no-dbb";
    let icon = false;
    let iconColor = null;
    let popupText = null;

    let hasFinalCarCheckDBBStatus = appointment.final_car_check_dbb_status > 0;
    let dbbStatus = hasFinalCarCheckDBBStatus ? appointment.final_car_check_dbb_status : appointment.car_check_dbb_status;
    let dbbStatusReason = hasFinalCarCheckDBBStatus ? appointment.final_car_check_dbb_status_reason : appointment.car_check_dbb_status_reason;

    if (appointment.car.has_dbb) {
      if (appointment.car.dbb_appointment_date) {
        if (moment().isAfter(appointment.car.dbb_appointment_date)) {
          status = "valid-appointment";
        } else {
          status = "waiting-appointment";
        }

        if (dbbStatus > 0) {
          icon = true;
          iconColor = dbbStatus === 1 ? "green" : dbbStatus === 2 ? "orange" : "red";

          if (dbbStatus === 2) {
            popupText = {
              bold: dbbStatusReason || t("miniportal_approval").message || "Miniportal is pending your approval,",
              normal: t("for_issues_reach_dbb_support").message || "for issues please reach out to DBB Support.",
            };
          } else if (dbbStatus === 3) {
            popupText = {
              bold: dbbStatusReason || t("unexpected_error").message || "An unexpected error has occurred,",
              normal: t("for_issues_reach_us").message || "for issues please reach out to us.",
            };
          }
        }
      } else {
        status = "no-appointment";

        if (this.hasStorageTyreBelowNecessaryTreadDepth()) {
          popupText = {
            bold: t("use_miniportal_to_adjust_tyres").message || "Use the miniportal to adjust the tyres that are delivered",
            normal: "",
          };
        }
      }
    }

    let canCreate = this.canCreateLocally();

    return (
      <Can I="update" the="dbb">
        <>
          <Button.Group basic fluid className="DBBButton">
            <Popup
              hoverable
              wide
              disabled={!popupText}
              content={
                popupText && (
                  <div className="DBBButton-popup">
                    <strong>{popupText.bold}</strong> {popupText.normal}
                  </div>
                )
              }
              trigger={
                <Button basic size="large" className={`-status-${status}`} onClick={this.handlePortalClick}>
                  {icon && (
                    <Label className="checkedTire" circular color={iconColor}>
                      <FontAwesomeIcon className="checkedTire-tire" icon={faTire} />
                      <FontAwesomeIcon className="checkedTire-check" icon={faCheck} />
                    </Label>
                  )}

                  <div className="button_text">
                    <div>DBB</div>
                    {status !== "no-dbb" && status !== "no-appointment" && (
                      <div className="button_app_date">{moment(appointment.car.dbb_appointment_date).format("DD-MM-YYYY")}</div>
                    )}
                  </div>

                  {isFetchingTyreLimits && <Loader active inline size="tiny" />}
                  {!isFetchingTyreLimits && canCreate && <Icon name="plus" color="green" />}
                  {!isFetchingTyreLimits && !canCreate && <Icon name="external" />}
                </Button>
              }
            />

            <Button basic icon onClick={this.handleRefreshClick}>
              <Icon name="refresh" />
            </Button>
          </Button.Group>

          <DBBAppointmentDateModal
            appointment={appointment}
            location={location}
            open={isDBBAppointmentDateModalOpen}
            onCarUpdate={onCarUpdate}
            onError={onError}
            onClose={() => this.setState({ isDBBAppointmentDateModalOpen: false })}
          />
        </>
      </Can>
    );
  }
}

export default withTranslation()(DBBButton);
